import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Activity, Shield, Zap, MapPin, BarChart3, ArrowRight } from "lucide-react";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

export default function LandingPage() {
  const { user, loading, trackEvent } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    trackEvent("landing_page_view", "/", document.referrer);
  }, []);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  if (loading) return null;

  const features = [
    {
      icon: Zap,
      title: "Real-Time Rates",
      desc: "Live bullion rates refreshed every second from 11+ dealers across India.",
    },
    {
      icon: MapPin,
      title: "Multi-City Coverage",
      desc: "Mumbai, Surat, Ahmedabad, Kolkata, Delhi, Chennai, Bangalore & more.",
    },
    {
      icon: BarChart3,
      title: "Smart Comparison",
      desc: "Instantly find the lowest bid with diff analysis against best rates.",
    },
    {
      icon: Shield,
      title: "Secure & Private",
      desc: "Enterprise-grade encryption. Your data stays protected at all times.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1C1917] flex flex-col" data-testid="landing-page">
      <SEO />
      {/* Nav */}
      <nav className="px-6 sm:px-12 py-5 flex items-center justify-between max-w-[1200px] mx-auto w-full">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-[#B48C36] rounded-lg flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading text-xl font-black tracking-tight">
            Bullion<span className="text-[#B48C36]">Track</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="text-[#78716C] hover:text-[#1C1917] text-xs"
            onClick={() => navigate("/terms")}
            data-testid="landing-terms-link"
          >
            Terms
          </Button>
          <Button
            onClick={() => navigate("/login")}
            className="bg-[#B48C36] hover:bg-[#9A7730] text-white font-bold px-5 h-9 text-sm"
            data-testid="landing-login-btn"
          >
            Login
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <main className="flex-1 flex flex-col justify-center px-6 sm:px-12 max-w-[1200px] mx-auto w-full">
        <section className="py-16 sm:py-24 lg:py-32" data-testid="hero-section">
          <div className="max-w-[700px]">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#B48C36]/8 border border-[#B48C36]/20 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#B48C36] animate-pulse" />
              <span className="text-[#B48C36] text-xs font-semibold tracking-wide uppercase">
                Live Now
              </span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.08] tracking-tight mb-5">
              India's Fastest
              <br />
              <span className="text-[#B48C36]">Bullion Rate</span>
              <br />
              Aggregator
            </h1>
            <p className="text-[#78716C] text-base sm:text-lg leading-relaxed max-w-[520px] mb-8">
              Track real-time rates from 11+ dealers across 8 cities. Find the lowest bid instantly.
              Built for bullion professionals.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => navigate("/login")}
                className="bg-[#B48C36] hover:bg-[#9A7730] text-white font-bold px-8 h-12 text-base gap-2"
                data-testid="hero-reserve-btn"
              >
                Reserve Access <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="pb-20 sm:pb-28" data-testid="features-section">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f) => (
              <div
                key={f.title}
                className="bg-white border border-[#E8E4DC] rounded-xl p-5 hover:border-[#B48C36]/40 hover:shadow-sm transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-[#FAF5EB] flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-[#B48C36]" />
                </div>
                <h3 className="font-heading font-bold text-sm mb-1.5 text-[#1C1917]">{f.title}</h3>
                <p className="text-[#78716C] text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
